import {
  OrderLineStatus,
  OrderLineType,
  OrderStatus,
  PaymentStatus,
  SortOrder,
} from '@/__generated__/graphql';
import { livesIdDocument } from '@/features/live/graphql/queries';
import { createShipmentDocument, deleteShipmentDocument } from '@/features/order/graphql/mutations';
import { shippingOrderDocument } from '@/features/order/graphql/queries';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  CloseButton,
  Divider,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDebouncedValue, useLocalStorage } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { IconLoader, IconMinus, IconPlus, IconPrinter, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable } from 'mantine-datatable';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useBarcodeScanner } from 'just-barcode-hook';

const orderStatusTranslations = {
  [OrderStatus.Created]: 'Vytvorená',
  [OrderStatus.Confirmed]: 'Potvrdená',
  [OrderStatus.Processed]: 'Spracovaná',
  [OrderStatus.Shipped]: 'Odoslaná',
  [OrderStatus.Delivered]: 'Doručená',
  [OrderStatus.Cancelled]: 'Zrušená',
  [OrderStatus.Reviewing]: 'V riešení',
  [OrderStatus.Returned]: 'Vrátená',
  [OrderStatus.Internal]: 'Interná',
  [OrderStatus.Reserved]: 'Rezervovaná',
};

const orderStatusColors = {
  [OrderStatus.Created]: 'gray',
  [OrderStatus.Confirmed]: 'blue',
  [OrderStatus.Processed]: 'cyan',
  [OrderStatus.Shipped]: 'teal',
  [OrderStatus.Delivered]: 'green',
  [OrderStatus.Cancelled]: 'red',
  [OrderStatus.Reviewing]: 'orange',
  [OrderStatus.Returned]: 'gray',
  [OrderStatus.Internal]: 'dark',
  [OrderStatus.Reserved]: 'indigo',
};

const paymentStatusColors = {
  [PaymentStatus.Pending]: 'gray',
  [PaymentStatus.Review]: 'orange',
  [PaymentStatus.Failed]: 'red',
  [PaymentStatus.Completed]: 'green',
};

const paymentStatusTranslations = {
  [PaymentStatus.Pending]: 'Čakajúca',
  [PaymentStatus.Review]: 'Nespárovaná',
  [PaymentStatus.Failed]: 'Zlyhala',
  [PaymentStatus.Completed]: 'Zaplatená',
};

const lineStatusColors = {
  [OrderLineStatus.Created]: 'gray',
  [OrderLineStatus.Packed]: 'blue',
  [OrderLineStatus.Confirmed]: 'green',
  [OrderLineStatus.Reviewing]: 'orange',
  [OrderLineStatus.Returned]: 'red',
};

const lineStatusTranslations = {
  [OrderLineStatus.Created]: 'Objednané',
  [OrderLineStatus.Packed]: 'Zabalené',
  [OrderLineStatus.Confirmed]: 'Potvrdené',
  [OrderLineStatus.Reviewing]: 'V riešení',
  [OrderLineStatus.Returned]: 'Vrátené',
};

const ProcessPersonalModal = ({
  orderId,
  packageNumber,
  name,
  total,
  onSuccess,
}: {
  orderId: string;
  packageNumber: number;
  name: string;
  total: number;
  onSuccess?: () => void;
}) => {
  return (
    <Stack>
      <Alert color="orange" mx="auto">
        <Text ta="center" fw={500}>
          Skontroluj či sedí! - OSOBNÝ ODBER
        </Text>
        <Title ta="center" fw={700}>
          {packageNumber} - {name} ({total} €)
        </Title>
      </Alert>
      <Button
        data-autofocus
        color="teal"
        onClick={async () => {
          modals.closeAll();

          showNotification({
            title: `Osobný odber ${packageNumber} potvrdený`,
            message: `Objednávka bola potvrdená pre osobný odber.`,
            color: 'green',
          });

          window
            .open(`https://api.laskykrasky.sk/api/order/processPickup?orderId=${orderId}`, '_blank')
            ?.focus();

          onSuccess?.();
        }}
      >
        Spracovať osobný odber
      </Button>
    </Stack>
  );
};

const CreateShipmentModal = ({
  orderId,
  packageNumber,
  name,
  total,
  onSuccess,
  autoFocus,
}: {
  orderId: string;
  packageNumber: number;
  name: string;
  total: number;
  onSuccess?: () => void;
  autoFocus?: boolean;
}) => {
  const [count, setCount] = useState(1);

  const [createShipment, { loading }] = useMutation(createShipmentDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onCompleted: () => {
      modals.closeAll();

      window.open(`https://api.laskykrasky.sk/api/gls/?orderId=${orderId}`, '_blank')?.focus();

      onSuccess?.();
    },
  });

  return (
    <Stack>
      <Alert color="orange" mx="auto">
        <Text ta="center" fw={500}>
          Skontroluj či sedí!
        </Text>
        <Title ta="center" fw={700}>
          {packageNumber} - {name} ({total} €)
        </Title>
      </Alert>
      <Group mx="auto">
        <ActionIcon onClick={() => setCount(count - 1)} disabled={count === 1}>
          <IconMinus size={16} />
        </ActionIcon>
        <Text>{count}</Text>
        <ActionIcon onClick={() => setCount(count + 1)}>
          <IconPlus size={16} />
        </ActionIcon>
      </Group>
      <Button
        {...(autoFocus && { 'data-autofocus': true })}
        loading={loading}
        onClick={async () => {
          modals.closeAll();

          await createShipment({ variables: { orderId, count } });

          showNotification({
            title: `Štítok vytvorený ${count}x`,
            message: `Štítok bol úspešne vytvorený`,
            color: 'green',
          });
        }}
      >
        Vytvoriť štítok
      </Button>
    </Stack>
  );
};

export const ShippingPage = () => {
  const scanner = useBarcodeScanner();

  const modalOpenRef = useRef(false);

  const [liveId, setLiveId] = useLocalStorage<string | null>({
    key: 'shipping-live-id',
    defaultValue: null,
  });
  const [inputPackageNumber, setInputPackageNumber] = useState<string | number>('');
  const [packageNumber] = useDebouncedValue(inputPackageNumber, 250);
  const packageNumberRef = useRef<HTMLInputElement>(null);

  const livesQuery = useQuery(livesIdDocument, {
    variables: {
      orderBy: { date: SortOrder.Desc },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const orderQuery = useQuery(shippingOrderDocument, {
    variables: {
      where: {
        liveId_package: {
          liveId: liveId ?? '',
          package: packageNumber !== '' ? +packageNumber : 0,
        },
      },
    },
    skip: !liveId || packageNumber === '',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const [fetchLazyOrder, lazyOrderQuery] = useLazyQuery(shippingOrderDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const [deleteShipment, { loading: deleting }] = useMutation(deleteShipmentDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const handleScan = useCallback(
    async (code: string) => {
      if (modalOpenRef.current) return;

      if (code === '')
        return showNotification({
          title: 'Neplatný kód',
          message: 'Naskenovaný kód je prázdny.',
          color: 'red',
        });

      const result = await fetchLazyOrder({ variables: { where: { id: code } } });

      if (!result.data?.order || result.error)
        return showNotification({
          title: 'Objednávka nenájdená',
          message: 'Skontrolujte naskenovaný kód.',
          color: 'red',
        });

      if (result.data.order.status !== OrderStatus.Confirmed)
        return showNotification({
          title: 'Objednávka nie je v potvrdenom stave',
          message: 'Objednávka musí byť v stave potvrdená pred vytvorením zásielky.',
          color: 'red',
        });

      if (!result.data.order.packedBy)
        return showNotification({
          title: 'Objednávka nie je zabalená',
          message: 'Objednávka musí byť zabalená pred vytvorením zásielky.',
          color: 'red',
        });

      if (result.data.order.shipment)
        return showNotification({
          title: 'Štítok už existuje',
          message: 'Štítok pre túto objednávku už bol vytvorený.',
          color: 'red',
        });

      if (
        (result.data.order.paymentMethod?.name === 'Prevod' ||
          result.data.order.paymentMethod?.name === 'Karta') &&
        (result.data.order.payments.some((p) => p.status !== PaymentStatus.Completed) ||
          result.data.order.payments.length === 0)
      )
        return showNotification({
          title: 'Platba nie je dokončená',
          message: 'Objednávka musí mať dokončenú platbu pred vytvorením zásielky.',
          color: 'red',
        });

      if (
        result.data.order.shippingMethod?.id === 'cm4bow5ql0005n82snqsojj6y' ||
        result.data.order.paymentMethod?.id === 'cm4bow5qp0009n82su9fyiisa'
      ) {
        window
          .open(
            `https://api.laskykrasky.sk/api/order/processPickup?orderId=${result.data.order.id}`,
            '_blank'
          )
          ?.focus();

        showNotification({
          title: `Osobný odber č. ${result.data.order.package} potvrdený`,
          message: `Objednávka bola potvrdená pre osobný odber.`,
          color: 'green',
        });

        return;
      }

      modalOpenRef.current = true;

      return modals.open({
        title: 'Vytvoriť zásielku',
        children: (
          <CreateShipmentModal
            orderId={result.data.order.id}
            packageNumber={result.data.order.package}
            name={result.data.order.customer.name}
            total={result.data.order.total}
          />
        ),
        closeOnClickOutside: false,
        onClose: () => (modalOpenRef.current = false),
      });
    },
    [fetchLazyOrder]
  );

  useEffect(() => {
    scanner.on(handleScan);

    return () => scanner.off();
  }, [fetchLazyOrder, handleScan, scanner]);

  const handleSubmit = () => {
    if (liveId === '')
      return showNotification({
        title: 'Nezadané údaje',
        message: 'Zadajte LIVE vysielanie.',
        color: 'red',
      });

    if (orderQuery.loading || !orderQuery.data?.order)
      return showNotification({
        title: 'Objednávka nenájdená',
        message: 'Skontrolujte číslo balíka.',
        color: 'red',
      });

    if (orderQuery.data.order.status !== OrderStatus.Confirmed)
      return showNotification({
        title: 'Objednávka nie je v potvrdenom stave',
        message: 'Objednávka musí byť v stave potvrdená pred vytvorením zásielky.',
        color: 'red',
      });

    if (!orderQuery.data.order.packedBy)
      return showNotification({
        title: 'Objednávka nie je zabalená',
        message: 'Objednávka musí byť zabalená pred vytvorením zásielky.',
        color: 'red',
      });

    if (orderQuery.data.order.shipment)
      return showNotification({
        title: 'Štítok už existuje',
        message: 'Štítok pre túto objednávku už bol vytvorený.',
        color: 'red',
      });

    if (
      (orderQuery.data.order.paymentMethod?.name === 'Prevod' ||
        orderQuery.data.order.paymentMethod?.name === 'Karta') &&
      (orderQuery.data.order.payments.some((p) => p.status !== PaymentStatus.Completed) ||
        orderQuery.data.order.payments.length === 0)
    )
      return showNotification({
        title: 'Platba nie je dokončená',
        message: 'Objednávka musí mať dokončenú platbu pred vytvorením zásielky.',
        color: 'red',
      });

    if (
      orderQuery.data.order.shippingMethod?.id === 'cm4bow5ql0005n82snqsojj6y' ||
      orderQuery.data.order.paymentMethod?.id === 'cm4bow5qp0009n82su9fyiisa'
    )
      return modals.open({
        title: 'Potvrdiť osobný odber',
        children: (
          <ProcessPersonalModal
            orderId={orderQuery.data.order.id}
            packageNumber={orderQuery.data.order.package}
            name={orderQuery.data.order.customer.name}
            total={orderQuery.data.order.total}
            onSuccess={() => {
              setInputPackageNumber('');
              packageNumberRef.current?.focus();
            }}
          />
        ),
        closeOnClickOutside: false,
      });

    modals.open({
      title: 'Vytvoriť zásielku',
      children: (
        <CreateShipmentModal
          orderId={orderQuery.data.order.id}
          packageNumber={orderQuery.data.order.package}
          name={orderQuery.data.order.customer.name}
          total={orderQuery.data.order.total}
          onSuccess={() => {
            setInputPackageNumber('');
            packageNumberRef.current?.focus();
          }}
          autoFocus={true}
        />
      ),
      closeOnClickOutside: false,
    });
  };

  const order = orderQuery.data?.order ?? lazyOrderQuery.data?.order;

  return (
    <Stack>
      <Paper p="md" withBorder component={Stack}>
        <Select
          clearable
          placeholder="LIVE..."
          data={livesQuery.data?.lives.map((live) => ({ value: live.id, label: live.name })) ?? []}
          value={liveId}
          onChange={setLiveId}
          onClear={() => {
            setLiveId('');
            setInputPackageNumber('');
          }}
          error={liveId === ''}
          disabled={livesQuery.loading}
          rightSection={livesQuery.loading && <IconLoader size={16} />}
        />
        <NumberInput
          ref={packageNumberRef}
          placeholder="Číslo objednávky..."
          allowNegative={false}
          allowDecimal={false}
          hideControls
          value={inputPackageNumber}
          onChange={setInputPackageNumber}
          disabled={!liveId || livesQuery.loading}
          error={
            !liveId || (packageNumber !== '' && !orderQuery.loading && !orderQuery.data?.order)
          }
          rightSection={orderQuery.loading && <IconLoader size={16} />}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;

            e.preventDefault();

            if (packageNumber === '')
              return showNotification({
                title: 'Nezadané údaje',
                message: 'Zadajte číslo balíka.',
                color: 'red',
              });

            handleSubmit();
          }}
        />
      </Paper>
      <Paper p="md" withBorder component={Stack}>
        {orderQuery.loading || lazyOrderQuery.loading ? (
          <Stack gap="sm">
            <Group>
              <Skeleton height={50} circle />
              <Stack gap={4} style={{ flex: 1 }}>
                <Skeleton height={12} width="60%" radius="xl" />
                <Skeleton height={12} width="30%" radius="xl" />
              </Stack>
            </Group>
            <Skeleton height={10} width="90%" radius="xl" />
            <Skeleton height={10} width="40%" radius="xl" />
            <Skeleton height={10} width="70%" radius="xl" />
          </Stack>
        ) : !order ? (
          <Stack gap="sm">
            <Text size="lg" fw={700} c="red">
              Objednávka nebola nájdená
            </Text>
            <Text size="sm" c="dimmed">
              Skontrolujte číslo balíka a zvolené vysielanie.
            </Text>
          </Stack>
        ) : (
          <Stack gap="xs">
            {lazyOrderQuery.data?.order && (
              <Alert color="orange">
                <Group justify="space-between" align="center">
                  <Text fw={500} flex={1}>
                    Táto objednávka bola otvorená zo skenera.
                  </Text>
                  {order.status === OrderStatus.Confirmed && order.packedBy && !order.shipment && (
                    <Button
                      variant="subtle"
                      onClick={() => handleScan(order.id)}
                      disabled={modalOpenRef.current}
                    >
                      Spracovať
                    </Button>
                  )}
                  <CloseButton
                    onClick={() =>
                      lazyOrderQuery.updateQuery((prev) => ({
                        ...prev,
                        order: null,
                      }))
                    }
                  />
                </Group>
              </Alert>
            )}
            <Group align="center" justify="space-between">
              <Avatar
                src={order.customer.avatar}
                name={order.customer.name}
                size="lg"
                radius="xl"
              />
              <Stack gap={4} align="center">
                <Group align="center">
                  <Text size="xl" fw={700}>
                    {order.package}
                  </Text>
                  <Text size="xl" fw={700}>
                    {order.customer.name}
                  </Text>
                  <Text size="xl" fw={700}>
                    {order.total} €
                  </Text>
                </Group>
                <Group align="center">
                  <Badge color={orderStatusColors[order.status]}>
                    {orderStatusTranslations[order.status]}
                  </Badge>
                  <Text size="lg" fw={500} c={!order.shippingMethod?.name ? 'orange' : undefined}>
                    {order.shippingMethod?.name ?? 'Spôsob dopravy nevyplnený'}
                  </Text>
                  <Text size="lg" fw={500} c={!order.paymentMethod?.name ? 'orange' : undefined}>
                    {order.paymentMethod?.name ?? 'Spôsob platby nevyplnený'}
                  </Text>
                </Group>
                <Group align="center">
                  <Text
                    c={
                      order.shipment || order.status === OrderStatus.Processed ? 'green' : 'orange'
                    }
                  >
                    {order.status === OrderStatus.Processed
                      ? 'Štítok na osobný odber bol vytvorený'
                      : order.shipment
                        ? 'Štítok už bol vytvorený'
                        : 'Štítok nie je vytvorený'}
                  </Text>
                </Group>
              </Stack>
              <Stack gap={0} align="flex-end">
                <Text c="dimmed">{dayjs(order.createdAt).format('DD.MM.YYYY HH:mm:ss')}</Text>
                <Text c="dimmed">{dayjs(order.updatedAt).format('DD.MM.YYYY HH:mm:ss')}</Text>
                <Text c={order.packedBy ? 'dimmed' : 'orange'}>
                  {order.packedBy ?? 'Nezabalené'}
                </Text>
              </Stack>
            </Group>

            <Divider />

            {order.payments.map((payment) => (
              <Group key={payment.id} align="center" justify="space-between">
                <Badge color={paymentStatusColors[payment.status]}>
                  {paymentStatusTranslations[payment.status]}
                </Badge>
                <Stack gap={0} align="center">
                  <Text fw={500}>{payment.method}</Text>
                  <Text c="dimmed">{payment.referenceId}</Text>
                </Stack>
                <Text fw={500}>{payment.amount} €</Text>
                <Stack gap={0} align="flex-end">
                  <Text size="sm" c="dimmed">
                    {dayjs(payment.createdAt).format('DD.MM.YYYY HH:mm:ss')}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {dayjs(payment.updatedAt).format('DD.MM.YYYY HH:mm:ss')}
                  </Text>
                </Stack>
              </Group>
            ))}

            <Divider />

            {order.shipment && (
              <Paper withBorder p="sm" component={Stack}>
                <Group align="center" justify="space-between">
                  <Stack gap={0}>
                    <Text size="sm" c="dimmed">
                      ID zásielky
                    </Text>
                    <Text size="sm">{order.shipment.externalId}</Text>
                  </Stack>
                  <Text>{order.shipment.parcels?.length} balík/ov</Text>
                  <Group>
                    {(order.shipment.parcels?.length ?? 0) > 0 && (
                      <ActionIcon
                        color="yellow"
                        onClick={() =>
                          modals.openConfirmModal({
                            size: 'auto',
                            title: 'Znova vytlačiť štítok',
                            children: (
                              <Alert color="orange">
                                <Text fz="xl" fw={600}>
                                  {order.package} – {order.customer.name} ({order.total} €)
                                </Text>
                                <Text fw={500}>
                                  Tento balík už má vytvorený GLS štítok (
                                  <Text span c="dimmed">
                                    {order.shipment?.externalId}
                                  </Text>
                                  ) o{' '}
                                  <Text span fw={600}>
                                    {dayjs(order.shipment?.createdAt).format('DD.MM.YYYY HH:mm:ss')}
                                  </Text>
                                  .
                                  <Text span fw={600} c="red">
                                    POZOR:
                                  </Text>{' '}
                                  Môže ísť o duplicitný štítok. Chceš ho vytlačiť znovu?
                                </Text>
                              </Alert>
                            ),
                            labels: { cancel: 'Nie', confirm: 'Vytlačiť znovu' },
                            confirmProps: { color: 'yellow' },
                            onConfirm: () =>
                              window
                                .open(
                                  `https://api.laskykrasky.sk/api/gls/print-only?orderId=${order.id}`,
                                  '_blank'
                                )
                                ?.focus(),
                          })
                        }
                      >
                        <IconPrinter size={16} />
                      </ActionIcon>
                    )}

                    <ActionIcon
                      color="red"
                      loading={deleting}
                      onClick={() =>
                        modals.openConfirmModal({
                          size: 'auto',
                          title: 'Zmazať zásielku',
                          children: (
                            <Alert color="red">
                              <Text fz="xl" fw={600}>
                                {order.package} - {order.customer.name} ({order.total} €)
                              </Text>
                              <Text fw={500}>
                                Chystáš sa zmazať zásielku GLS (
                                <Text span c="dimmed">
                                  {order.shipment?.externalId}
                                </Text>
                                ) vrátane všetkých balíkov ({order.shipment?.parcels?.length ?? 0}{' '}
                                ks).
                                <br />
                                <Text span fw={600} c="red">
                                  POZOR:
                                </Text>{' '}
                                Táto operácia odstráni údaje zo systému aj z GLS a nastaví
                                objednávku späť na kontrolu. Pokračovať?
                              </Text>
                            </Alert>
                          ),
                          labels: { cancel: 'Nie', confirm: 'Vymazať zásielku' },
                          confirmProps: { color: 'red' },
                          onConfirm: async () => {
                            await deleteShipment({ variables: { orderId: order.id } });
                            showNotification({
                              color: 'red',
                              title: 'Zásielka bola zmazaná',
                              message: `Objednávka č. ${order.package} bola vrátená na kontrolu.`,
                            });
                          },
                        })
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                  <Stack gap={0} align="flex-end">
                    <Text size="sm" c="dimmed">
                      {dayjs(order.shipment.createdAt).format('DD.MM.YYYY HH:mm:ss')}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {dayjs(order.shipment.updatedAt).format('DD.MM.YYYY HH:mm:ss')}
                    </Text>
                  </Stack>
                </Group>
                <Divider />
                {order.shipment.parcels?.map((parcel, i) => (
                  <Paper p="sm" key={parcel.id} gap="xs" component={Stack} withBorder>
                    <Group align="center" justify="space-between">
                      <Stack gap={0}>
                        <Text size="sm" c="dimmed">
                          ID (#)
                        </Text>
                        <Text size="sm">
                          {parcel.externalId} ({i + 1}/{order.shipment?.parcels?.length})
                        </Text>
                      </Stack>
                      <Stack gap={0}>
                        <Text size="sm" c="dimmed">
                          Sledovacie číslo
                        </Text>
                        <Text size="sm">{parcel.trackingNumber}</Text>
                      </Stack>
                      <Stack gap={0}>
                        <Text size="sm" c="dimmed">
                          Váha (kg)
                        </Text>
                        <Text size="sm">{parcel.weight ?? '-'}</Text>
                      </Stack>
                      <Stack gap={0} align="flex-end">
                        <Text size="sm" c="dimmed">
                          {dayjs(parcel.createdAt).format('DD.MM.YYYY HH:mm:ss')}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {dayjs(parcel.updatedAt).format('DD.MM.YYYY HH:mm:ss')}
                        </Text>
                      </Stack>
                    </Group>
                    {(parcel.statuses?.length ?? 0) > 0 && (
                      <Table
                        striped
                        highlightOnHover
                        withTableBorder
                        data={{
                          body: parcel.statuses?.map((s) => [
                            s.statusInfo ?? '-',
                            s.statusCode,
                            s.statusDescription ?? '-',
                            `${s.depotCity} (${s.depotNumber})`,
                            dayjs(s.statusDate).format('DD.MM.YYYY HH:mm:ss'),
                          ]),
                        }}
                      />
                    )}
                  </Paper>
                ))}
              </Paper>
            )}

            <Grid>
              <Grid.Col span={8}>
                <DataTable
                  withTableBorder
                  striped
                  highlightOnHover
                  textSelectionDisabled
                  borderRadius="sm"
                  records={order.lines ?? []}
                  minHeight={200}
                  columns={[
                    {
                      accessor: 'code',
                      title: 'Kód',
                      render: ({ code, type }) => (type === OrderLineType.Product ? code : ''),
                    },
                    { accessor: 'name', title: 'Názov' },
                    {
                      accessor: 'description',
                      title: 'Popis',
                    },
                    {
                      accessor: 'price',
                      title: 'Cena',
                      render: ({ price }) => `${price} €`,
                    },
                    {
                      accessor: 'status',
                      title: 'Stav',
                      render: ({ status }) => (
                        <Badge color={lineStatusColors[status]}>
                          {lineStatusTranslations[status]}
                        </Badge>
                      ),
                    },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <Paper withBorder p="md">
                  <Stack gap={0} justify="flex-end">
                    <Text>{order.address?.name}</Text>
                    <Text>{order.address?.streetLine}</Text>
                    {order.address?.streetDescription && (
                      <Text c="dimmed">{order.address.streetDescription}</Text>
                    )}
                    <Text>
                      {order.address?.postCode} {order.address?.city}
                    </Text>
                    <Text>
                      {order.address?.country} {order.address?.phone}
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};
